import { Alert, Box, Tooltip, Typography } from "@mui/material";
import {
	MinFlexnetVersionForPnRemoval,
	UnsupportedFlexNetPlant,
} from "lib/flexnet/partNumberGate";

interface flexNetPartNumberWarningProps {
	unsupportedPlants: UnsupportedFlexNetPlant[];
}

/**
 * FlexNet version-gate warning. Intro states the required FlexNet version;
 * plant names only in the list — hover shows the on-record version when present.
 */
export function FlexNetPartNumberWarning({
	unsupportedPlants,
}: flexNetPartNumberWarningProps): React.JSX.Element | null {
	if (!unsupportedPlants.length) return null;

	const warningIntro = `One or more selected plants require FlexNet ${MinFlexnetVersionForPnRemoval} for Part Number removal:`;

	return (
		<Box data-test-selector="flexnet-part-number-gate-warning" sx={{ width: "100%" }}>
			<Alert
				severity="warning"
				sx={{
					mb: 0,
					alignItems: "flex-start",
					"& .MuiAlert-message": { width: "100%" },
				}}
			>
				<Typography
					component="p"
					sx={{
						m: 0,
						fontSize: "0.875rem",
						fontWeight: 400,
						lineHeight: 1.4,
					}}
				>
					{warningIntro}
				</Typography>
				<Box
					component="ul"
					sx={{
						m: 0,
						mt: 1,
						pl: 2.5,
						mb: 0,
					}}
				>
					{unsupportedPlants.map((p) => {
						const plantName = (
							<Typography
								component="span"
								tabIndex={p.flexNetVersion ? 0 : undefined}
								data-test-selector="flexnet-on-record-version"
								data-flexnet-on-record={p.flexNetVersion ?? ""}
								sx={{
									fontSize: "0.875rem",
									lineHeight: 1.5,
									fontWeight: 700,
									cursor: "default",
									width: "fit-content",
								}}
							>
								{p.name}
							</Typography>
						);

						return (
							<li key={p.id ?? `${p.name}-${p.flexNetVersion ?? "none"}`}>
								{p.flexNetVersion ? (
									<Tooltip title={p.flexNetVersion} placement="top" arrow>
										{plantName}
									</Tooltip>
								) : (
									plantName
								)}
							</li>
						);
					})}
				</Box>
			</Alert>
		</Box>
	);
}
